import axios from "axios";
import { baseUrl } from "utils/baseUrl";
import { getSubdomainInfo } from "utils/subdomainDetection";
import { validateSpeakerExists } from "./authGuardLoader";

// Marker class so catch blocks can distinguish our errors from axios/network errors
class LoaderError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "LoaderError";
  }
}

export async function bookingsGuardLoader({
  request,
  params,
}: {
  request: Request;
  params: Record<string, string | undefined>;
}): Promise<null> {
  const url = new URL(request.url);

  // Resolve username the same way validateSpeakerExists does
  const subdomainInfo = getSubdomainInfo();
  const username =
    subdomainInfo.isUserSubdomain && subdomainInfo.username
      ? subdomainInfo.username
      : url.searchParams.get("username");

  // Validate speaker — re-throw with a username-aware message
  try {
    await validateSpeakerExists(url);
  } catch {
    throw new LoaderError(
      username
        ? `No speaker was found for "@${username}".`
        : "The speaker profile you're looking for doesn't exist."
    );
  }

  // Validate the specific booking when bookingId is present
  const { bookingId } = params;
  if (bookingId && username) {
    try {
      const response = await axios.get(
        `${baseUrl}/Bookings/Calendar?username=${username}&id=${bookingId}`
      );
      const calendar = response.data?.calendar;

      if (!calendar) {
        throw new LoaderError(
          "The meeting link you entered is invalid or no longer exists."
        );
      }

      if (calendar.deleted === true) {
        throw new LoaderError("This meeting link has been suspended.");
      }
    } catch (error) {
      // Re-throw our own errors; convert API/network errors to a generic message
      if (error instanceof LoaderError) throw error;
      throw new LoaderError(
        "The meeting link you entered is invalid or no longer exists."
      );
    }
  }

  return null;
}
